import React, { useState, useEffect, useRef, useCallback } from 'react';
import socket from '../socket';
import { IconHash, IconWorkspace } from './Icons';
import './SearchPanel.css';

function highlightMatch(text, query) {
  if (!text || !query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part,
  );
}

function snippet(text, query, maxLen = 140) {
  if (!text) return '';
  const lower = text.toLowerCase();
  const idx = lower.indexOf(query.toLowerCase());
  if (idx === -1) return text.slice(0, maxLen);
  const start = Math.max(0, idx - 40);
  const end = Math.min(text.length, idx + query.length + 100);
  let s = text.slice(start, end);
  if (start > 0) s = '...' + s;
  if (end < text.length) s = s + '...';
  return s;
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function SearchPanel({
  workspaceId,
  onNavigate,
  onClose,
  inline = false,
  autoFocus = true,
  // When set, the panel renders a scope toggle and defaults to thread-only.
  // Passing roomName lets the toggle label show the thread name.
  roomId = null,
  roomName = null,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  // 'thread' or 'workspace'. Default to thread when a roomId is provided.
  const [scope, setScope] = useState(roomId ? 'thread' : 'workspace');
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const debounceRef = useRef(null);

  // Keep scope honest if the parent flips roomId on/off.
  useEffect(() => {
    if (!roomId && scope === 'thread') setScope('workspace');
  }, [roomId, scope]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Inline mode has no overlay — dismiss the dropdown when the user
  // taps anywhere outside the bar.
  useEffect(() => {
    if (!inline) return;
    if (!query && !results) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setResults(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [inline, query, results]);

  const doSearch = useCallback(
    (q, activeScope) => {
      if (!q.trim() || !workspaceId) {
        setResults(null);
        return;
      }
      setLoading(true);
      const payload = { query: q.trim(), workspaceId, limit: 20 };
      if (activeScope === 'thread' && roomId) payload.roomId = roomId;
      socket.emit('search', payload, (res) => {
        setLoading(false);
        if (res?.success) {
          setResults(res.results);
        }
      });
    },
    [workspaceId, roomId],
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val, scope), 250);
  };

  const handleScopeChange = (next) => {
    if (next === scope) return;
    setScope(next);
    // Re-run the current query against the new scope so results update immediately.
    if (query.trim().length > 0) doSearch(query, next);
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    if (onClose) onClose();
    else {
      setQuery('');
      setResults(null);
      inputRef.current?.blur();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

  const handleResultClick = (resThreadId) => {
    if (onClose) onClose();
    onNavigate(resThreadId);
  };

  const hasQuery = query.trim().length > 0;

  const totalCount =
    (results?.messages?.length || 0) +
    (results?.artifacts?.length || 0) +
    (results?.decisions?.length || 0);

  const placeholder =
    scope === 'thread' && roomName
      ? `Search this thread...`
      : 'Search messages, artifacts, decisions...';

  const panel = (
    <div className={`search-panel ${inline ? 'inline' : ''}`} ref={panelRef}>
      <div className="search-input-row">
        <span className="search-icon" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {roomId && (
          <div className="search-scope-toggle" role="group" aria-label="Search scope">
            <button
              type="button"
              className={`search-scope-chip search-scope-icon ${scope === 'thread' ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleScopeChange('thread')}
              title={roomName ? `Search only within "${roomName}"` : 'Search only this thread'}
              aria-label="Search this thread"
            >
              <IconHash size={13} />
            </button>
            <button
              type="button"
              className={`search-scope-chip search-scope-icon ${scope === 'workspace' ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleScopeChange('workspace')}
              title="Search across the entire workspace"
              aria-label="Search workspace"
            >
              <IconWorkspace size={13} />
            </button>
          </div>
        )}
        {!inline && <span className="search-shortcut">Esc</span>}
      </div>

      {(!inline || hasQuery || loading) && (
        <div className="search-body">
          {loading && <div className="search-loading">Searching...</div>}

          {!loading && !results && query.length === 0 && (
            <div className="search-empty">
              {scope === 'thread' && roomName
                ? `Type to search within "${roomName}"`
                : 'Type to search across this workspace'}
            </div>
          )}

          {!loading && results && totalCount === 0 && (
            <div className="search-empty">No results for "{query}"</div>
          )}

          {!loading && results && results.messages?.length > 0 && (
            <div className="search-section">
              <div className="search-section-title">Messages ({results.messages.length})</div>
              {results.messages.map((msg) => (
                <div
                  key={msg.id}
                  className="search-result"
                  onClick={() => handleResultClick(msg.roomId)}
                >
                  <div className="search-result-header">
                    <span className="search-type-badge message">msg</span>
                    <span className="search-result-room">{msg.roomName}</span>
                    <span className="search-result-meta">
                      {msg.senderName} &middot; {formatTime(msg.timestamp)}
                    </span>
                  </div>
                  <div className="search-result-snippet">
                    {highlightMatch(snippet(msg.content, query), query)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && results && results.artifacts?.length > 0 && (
            <div className="search-section">
              <div className="search-section-title">Artifacts ({results.artifacts.length})</div>
              {results.artifacts.map((art) => (
                <div
                  key={art.id}
                  className="search-result"
                  onClick={() => handleResultClick(art.roomId)}
                >
                  <div className="search-result-header">
                    <span className="search-type-badge artifact">{art.type}</span>
                    <span className="search-result-room">{art.roomName}</span>
                    <span className="search-result-meta">{formatTime(art.createdAt)}</span>
                  </div>
                  <div className="search-result-title">{highlightMatch(art.title, query)}</div>
                  <div className="search-result-snippet">
                    {highlightMatch(snippet(art.content, query), query)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && results && results.decisions?.length > 0 && (
            <div className="search-section">
              <div className="search-section-title">Decisions ({results.decisions.length})</div>
              {results.decisions.map((dec) => (
                <div
                  key={dec.id}
                  className="search-result"
                  onClick={() => handleResultClick(dec.roomId)}
                >
                  <div className="search-result-header">
                    <span className="search-type-badge decision">decision</span>
                    <span className="search-result-room">{dec.roomName}</span>
                    <span className={`search-status ${dec.status}`}>{dec.status}</span>
                    <span className="search-result-meta">{formatTime(dec.decidedAt)}</span>
                  </div>
                  <div className="search-result-title">{highlightMatch(dec.title, query)}</div>
                  <div className="search-result-snippet">
                    {highlightMatch(snippet(dec.description, query), query)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (inline) return panel;

  return (
    <div className="search-overlay" onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      {panel}
    </div>
  );
}

export default SearchPanel;
